import { type MediaLibraryItem, type MediaLibraryResourceType } from "@fitness-app/data-models/entities/MediaLibrary";

import { getLoggedUser } from "../../../helpers/getLoggedUser";
import { type AppThunk } from "../../../index";
import {
  addFileToList,
  deleteFileFromList,
  subscribeToMediaLibraryFailed,
  subscribeToMediaLibraryPending,
  subscribeToMediaLibrarySuccess,
  updateFileOnList,
} from "../reducer";
import { fetchMediaLibraryFiles } from "./fetchMediaLibraryFiles";
import { unsubscribeFromMediaLibrary } from "./unsubscribeFromMediaLibrary";
import { openRealtimeChannel } from "../../../helpers/openRealtimeChannel";
import { claimRealtimeSlot, ownsRealtimeSlot } from "../../../helpers/realtimeSlot";

export const subscribeToMediaLibrary =
  (type: MediaLibraryResourceType): AppThunk =>
  async (dispatch, getState, { auth, subscriptions, db }) => {
    const claimedSlot = claimRealtimeSlot(subscriptions, "mediaLibrary");

    try {
      dispatch(subscribeToMediaLibraryPending());

      await dispatch(fetchMediaLibraryFiles(type));

      const { id } = await getLoggedUser(auth);

      // a newer subscribe or an unsubscribe took the slot while we were fetching
      if (!ownsRealtimeSlot(subscriptions, "mediaLibrary", claimedSlot)) {
        return;
      }

      const channelName = `media-library-${id}`;
      const channel = await openRealtimeChannel(db, channelName, subscriptions.realtimeMediaLibraryListChannel);

      // detaching the previous channel is another await - the slot may have changed hands
      if (!ownsRealtimeSlot(subscriptions, "mediaLibrary", claimedSlot)) {
        await db.removeChannel(channel);
        return;
      }

      subscriptions.mediaLibraryListChannelName = channelName;
      subscriptions.realtimeMediaLibraryListChannel = channel;

      subscriptions.realtimeMediaLibraryListChannel
        .on<MediaLibraryItem>(
          "postgres_changes",
          { event: "*", schema: "public", table: "media_library" },
          (payload) => {
            if (payload.eventType === "UPDATE") {
              dispatch(updateFileOnList(payload.new));
            }
            if (payload.eventType === "INSERT") {
              if (payload.new.resourceType !== type) {
                // skip if the resource type is not the same as the one we are interested in
              } else {
                dispatch(addFileToList(payload.new));
              }
            }
            if (payload.eventType === "DELETE" && payload.old.id) {
              dispatch(deleteFileFromList(payload.old.id));
            }
          },
        )
        .subscribe((status) => {
          if (status === "SUBSCRIBED") {
            dispatch(subscribeToMediaLibrarySuccess());
          }
          if (status === "TIMED_OUT") {
            dispatch(unsubscribeFromMediaLibrary());
          }
        });
    } catch {
      dispatch(subscribeToMediaLibraryFailed());
    }
  };
